<template>
  <div class="p2p-page narrow">
    <div class="p2p-stack" style="gap:6px">
      <h1 class="p2p-h1">Publishing controls</h1>
      <p class="p2p-lead">Site-wide switches for publishing and autopilot, plus the AI spending budget.</p>
    </div>
    <div v-if="error" class="p2p-alert bad">{{ error }}</div>
    <div v-if="notice" class="p2p-alert good">{{ notice }}</div>
    <section v-if="state" class="p2p-card">
      <h2 class="p2p-h2">Publishing</h2>
      <div class="p2p-row" style="gap:8px">
        <span :class="['p2p-chip', state.providers.postforme ? 'good' : 'warn']">Post for Me {{ state.providers.postforme ? (state.providers.demo ? 'demo mode' : state.providers.simulator ? 'local simulator' : 'connected') : 'not configured' }}</span>
        <span :class="['p2p-chip', state.providers.gemini ? 'good' : 'warn']">AI video {{ state.providers.gemini ? 'available' : 'not configured' }}</span>
      </div>
      <label class="p2p-check"><input v-model="form.publishing_paused" type="checkbox"> Pause all publishing (scheduled posts wait)</label>
      <label class="p2p-check"><input v-model="form.auto_publish_paused" type="checkbox"> Pause autopilot runs</label>
      <div class="p2p-label">Networks paused
        <div class="p2p-row" style="gap:6px">
          <label v-for="(label, key) in state.platforms" :key="key" class="p2p-check p2p-chip"><input v-model="form.disabled_platforms" type="checkbox" :value="key"> {{ label }}</label>
        </div>
      </div>
      <label class="p2p-label">Reason (recorded)<input v-model="form.reason" class="p2p-input" maxlength="240"></label>
      <button type="button" class="p2p-btn" style="align-self:flex-start" :disabled="busy || form.reason.trim().length < 5" @click="save">Save controls</button>
      <div v-if="Object.keys(state.deliveries_7d).length" class="p2p-stack" style="gap:6px">
        <span class="p2p-kicker">Last 7 days</span>
        <table class="p2p-table"><thead><tr><th>Network</th><th>Published</th><th>Failed</th><th>Other</th></tr></thead>
          <tbody><tr v-for="(counts, platform) in state.deliveries_7d" :key="platform"><td>{{ state.platforms[platform] || platform }}</td>
            <td>{{ counts.published || 0 }}</td><td>{{ counts.failed || 0 }}</td><td>{{ other(counts) }}</td></tr></tbody></table>
      </div>
    </section>
    <section v-if="state && state.controls && wl" class="p2p-card" aria-labelledby="own-apps-title">
      <h2 id="own-apps-title" class="p2p-h2">Your own app credentials</h2>
      <p class="p2p-text">By default people connect through Post for Me's apps, so each network's sign-in page names Post for Me. Once a network approves your own developer app and its credentials are saved in your Post for Me project, switch that network on here: its sign-in page then names your app.</p>
      <div v-for="(label, key) in state.platforms" :key="'own-' + key" class="p2p-row" style="gap:10px;align-items:center">
        <label class="p2p-check" style="min-width:170px"><input v-model="wl[key].on" type="checkbox"> {{ label }}</label>
        <select v-if="key === 'x' && wl.x.on" v-model="wl.x.connection" class="p2p-select" style="max-width:220px" aria-label="X app type">
          <option value="oauth2">OAuth 2.0 app</option><option value="oauth1">OAuth 1.0a app</option>
        </select>
        <select v-if="key === 'linkedin' && wl.linkedin.on" v-model="wl.linkedin.connection" class="p2p-select" style="max-width:300px" aria-label="LinkedIn connection">
          <option value="organization">Company pages (Community Management API)</option><option value="personal">Personal profiles</option>
        </select>
      </div>
      <label class="p2p-label">Reason (recorded)<input v-model="wlReason" class="p2p-input" maxlength="240"></label>
      <button type="button" class="p2p-btn sm" style="align-self:flex-start" :disabled="busy || wlReason.trim().length < 5" @click="saveWhiteLabel">Save app credentials setting</button>
    </section>

    <section v-if="state" class="p2p-card">
      <h2 class="p2p-h2">Post for Me webhook</h2>
      <p class="p2p-text">Post for Me tells this app about sign-outs and post results through a webhook. Register this API's address once; Post for Me then returns a signing secret for the server settings.</p>
      <div v-if="hooks && !hooks.available" class="p2p-muted">Available once a Post for Me project key is set.</div>
      <template v-else-if="hooks">
        <div v-if="!hooks.webhooks.length" class="p2p-muted">No webhook is registered yet.</div>
        <div v-for="hook in hooks.webhooks" :key="hook.id" class="p2p-row" style="gap:8px">
          <span :class="['p2p-chip', hook.url === hooks.suggested_url ? 'good' : '']">{{ hook.url === hooks.suggested_url ? 'This API' : 'Other' }}</span>
          <span class="p2p-mono p2p-faint" style="overflow-wrap:anywhere">{{ hook.url }}</span>
        </div>
        <span :class="['p2p-faint', { 'p2p-bad': !hooks.secret_configured }]">Signing secret on this server: {{ hooks.secret_configured ? 'set' : 'not set yet' }}</span>
        <template v-if="!registered">
          <label class="p2p-label">Webhook address<input v-model="hookForm.url" class="p2p-input"></label>
          <label class="p2p-label">Reason (recorded)<input v-model="hookForm.reason" class="p2p-input" maxlength="240"></label>
          <button type="button" class="p2p-btn sm" style="align-self:flex-start" :disabled="busy || hookForm.reason.trim().length < 5" @click="registerHook">Register webhook</button>
        </template>
        <div v-else class="p2p-alert">
          <div class="p2p-stack" style="gap:6px">
            <strong>Registered. Copy the signing secret now: it is not shown again.</strong>
            <code class="p2p-mono" style="overflow-wrap:anywhere">{{ registered.secret || '(Post for Me returned no secret)' }}</code>
            <span>{{ registered.secret_matches ? 'It already matches this server\'s setting.' : 'Set SITE_PUBLISHER_POSTFORME_WEBHOOK_SECRET to it on the server, then restart.' }}</span>
          </div>
        </div>
      </template>
    </section>

    <section v-if="canary" class="p2p-card">
      <h2 class="p2p-h2">Daily delivery check</h2>
      <p class="p2p-text">Once a day after the chosen hour (UTC), a short test post goes to each account below, through the same path as real posts. Any failure is reported to the site admins. Use test accounts you connected yourself: the posts are public.</p>
      <div v-if="!canary.accounts.length" class="p2p-muted">Connect a test account in Connections first.</div>
      <div v-else class="p2p-row" style="gap:6px">
        <label v-for="account in canary.accounts" :key="account.id" class="p2p-check p2p-chip">
          <input v-model="canaryForm.account_ids" type="checkbox" :value="account.id"> {{ account.label }} · {{ account.username }}
        </label>
      </div>
      <div class="p2p-row" style="align-items:flex-end">
        <label class="p2p-label">Hour (UTC)<input v-model.number="canaryForm.hour" type="number" min="0" max="23" class="p2p-input" style="width:110px"></label>
        <label class="p2p-label" style="flex:1 1 260px">Reason (recorded)<input v-model="canaryForm.reason" class="p2p-input" maxlength="240"></label>
      </div>
      <div class="p2p-row">
        <button type="button" class="p2p-btn sm" :disabled="busy || canaryForm.reason.trim().length < 5" @click="saveCanary">Save check settings</button>
        <button type="button" class="p2p-btn sm soft" :disabled="busy || !canary.account_ids.length" @click="runCanary">Run today's check now</button>
      </div>
      <table v-if="canary.history.length" class="p2p-table">
        <thead><tr><th>Day</th><th>Network</th><th>Result</th></tr></thead>
        <tbody><tr v-for="item in canary.history.slice(0, 14)" :key="item.id">
          <td class="p2p-mono">{{ item.date }}</td><td>{{ item.label }}</td>
          <td><span :class="['p2p-chip', item.state === 'published' ? 'good' : item.state === 'failed' ? 'bad' : '']">{{ canaryLabel(item.state) }}</span>
            <a v-if="item.public_url" :href="item.public_url" target="_blank" rel="noopener" class="p2p-faint"> view</a>
            <span v-if="item.error" class="p2p-faint p2p-bad"> {{ item.error }}</span></td>
        </tr></tbody>
      </table>
    </section>

    <section v-if="spend" class="p2p-card">
      <h2 class="p2p-h2">AI spending</h2>
      <p class="p2p-text">Writing, AI pictures, site reading and transcribing uploaded videos are paid OpenAI calls, bounded by these limits. A limit of 0 means unlimited.</p>
      <label class="p2p-check"><input v-model="spendForm.enabled" type="checkbox"> Generation enabled</label>
      <label class="p2p-check"><input v-model="spendForm.paused" type="checkbox"> Paused</label>
      <div class="p2p-row">
        <label class="p2p-label">Daily limit (USD)<input v-model="spendForm.daily_limit_usd" class="p2p-input"></label>
        <label class="p2p-label">Per operation (USD)<input v-model="spendForm.operation_limit_usd" class="p2p-input"></label>
      </div>
      <label class="p2p-label">Reason (recorded)<input v-model="spendForm.reason" class="p2p-input" maxlength="240"></label>
      <button type="button" class="p2p-btn" style="align-self:flex-start" :disabled="busy || spendForm.reason.trim().length < 10" @click="saveSpend">Save spending limits</button>
    </section>
  </div>
</template>

<script>
import { get, put, post, message } from '../api';

export default {
  name: 'AdminPublishing',
  data: () => ({ state: null, spend: null, busy: false, error: '', notice: '', hooks: null, registered: null, canary: null,
    wl: null, wlReason: '',
    hookForm: { url: '', reason: '' }, canaryForm: { account_ids: [], hour: 9, reason: '' },
    form: { publishing_paused: false, auto_publish_paused: false, disabled_platforms: [], reason: '' },
    spendForm: { enabled: false, paused: false, daily_limit_usd: '0', operation_limit_usd: '0', reason: '' } }),
  mounted() { this.load(); },
  methods: {
    other(counts) { return Object.keys(counts).filter(key => key !== 'published' && key !== 'failed').reduce((sum, key) => sum + counts[key], 0); },
    async load() {
      try {
        this.state = await get('/admin/controls');
        if (this.state.controls) {
          const c = this.state.controls;
          this.form = { publishing_paused: c.publishing_paused, auto_publish_paused: c.auto_publish_paused, disabled_platforms: c.disabled_platforms.slice(), reason: '' };
          this.setWhiteLabel(c.white_label || {});
        }
        this.spend = await get('/admin/provider-spend', { days: 7 });
        this.hooks = await get('/admin/support/webhooks').catch(() => null);
        if (this.hooks && !this.hookForm.url) this.hookForm.url = this.hooks.suggested_url;
        this.setCanary(await get('/admin/support/canary'));
        const controls = this.spend.controls || {};
        this.spendForm = { enabled: !!controls.enabled, paused: !!controls.paused, daily_limit_usd: String(controls.daily_limit_usd || '0'),
          operation_limit_usd: String(controls.operation_limit_usd || '0'), reason: '' };
      } catch (error) {
        this.error = message(error);
      }
    },
    async save() {
      this.busy = true;
      this.error = '';
      try {
        this.state = await put('/admin/controls', { ...this.form, revision: this.state.controls.revision });
        this.notice = 'Publishing controls saved.';
        this.form.reason = '';
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    },
    setWhiteLabel(saved) {
      const defaults = { x: 'oauth2', linkedin: 'organization' };
      const value = {};
      Object.keys(this.state.platforms).forEach(key => {
        value[key] = { on: !!saved[key], connection: (saved[key] && saved[key].connection) || defaults[key] || null };
      });
      this.wl = value;
    },
    async saveWhiteLabel() {
      this.busy = true;
      this.error = '';
      try {
        const networks = {};
        Object.keys(this.wl).filter(key => this.wl[key].on).forEach(key => {
          networks[key] = { connection: key === 'x' || key === 'linkedin' ? this.wl[key].connection : null };
        });
        this.state = await put('/admin/white-label', { networks, reason: this.wlReason, revision: this.state.controls.revision });
        this.setWhiteLabel(this.state.controls.white_label || {});
        this.wlReason = '';
        this.notice = 'App credentials setting saved. New connections use it at once.';
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    },
    setCanary(value) {
      this.canary = value;
      this.canaryForm = { account_ids: value.account_ids.slice(), hour: value.hour, reason: '' };
    },
    canaryLabel(state) { return { published: 'Passed', failed: 'Failed', submitted: 'Checking', creating: 'Sending', pending: 'Waiting' }[state] || state; },
    async registerHook() {
      this.busy = true;
      this.error = '';
      try {
        this.registered = await post('/admin/support/webhooks', this.hookForm);
        this.hooks = await get('/admin/support/webhooks');
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    },
    async saveCanary() {
      this.busy = true;
      this.error = '';
      try {
        this.setCanary(await put('/admin/support/canary', { ...this.canaryForm, revision: this.canary.revision }));
        this.notice = 'Delivery check settings saved.';
        this.state = await get('/admin/controls');
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    },
    async runCanary() {
      this.busy = true;
      this.error = '';
      try {
        const result = await post('/admin/support/canary/run');
        this.setCanary(result);
        this.notice = 'Today\'s check started on ' + result.report.started + ' account' + (result.report.started === 1 ? '' : 's') + '. Results arrive within a minute or two.';
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    },
    async saveSpend() {
      this.busy = true;
      this.error = '';
      try {
        await put('/admin/provider-spend', { ...this.spendForm, revision: this.spend.controls.revision });
        this.notice = 'Spending limits saved.';
        await this.load();
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
